import Vue from 'vue'
import moment from 'moment'
import config from '@/config'
import { getCachedFileDownloadToken, getFileDownloadToken } from './file-token'

/** 文件下载链接的来源标识（＝包名），与 token 一起拼在下载地址上，便于后端区分调用来源 */
const FILE_SOURCE = 'main-micro'

/**
 * 幂等追加 URL 查询参数（已存在同名参数则覆盖其值）
 * @param url 目标地址
 * @param key 参数名
 * @param value 参数值
 */
function appendQuery(url: string, key: string, value: string): string {
  if (!url || !value) return url
  const re = new RegExp(`([?&])${key}=[^&#]*`)
  const seg = `${key}=${encodeURIComponent(value)}`
  return re.test(url) ? url.replace(re, `$1${seg}`) : `${url}${url.includes('?') ? '&' : '?'}${seg}`
}

/**
 * 获取下载文件地址
 * @param code 附件code
 */
export function getDownloadURL(code: string) {
  const token = getCachedFileDownloadToken()
  return appendQuery(
    `${config.get('SERVER_URL')}api/file/download?code=${code}&token=${encodeURIComponent(token)}`,
    'source',
    FILE_SOURCE,
  )
}

/**
 * 获取下载文件地址（异步版本，Token 不存在或临近过期时自动刷新）
 * 适用于点击类操作，避免使用本地缓存中已过期的 Token 拼接地址导致 401
 * @param code 附件code 或 完整的 http(s) 下载地址
 */
export async function getDownloadURLWithToken(code: string): Promise<string> {
  if (!code) return ''
  const token = await getFileDownloadToken()
  const startWithHttp: boolean = /(http|https)/.test(code)
  if (startWithHttp) {
    const sep = code.includes('?') ? '&' : '?'
    return appendQuery(`${code}${sep}token=${encodeURIComponent(token)}`, 'source', FILE_SOURCE)
  }
  const baseUrl = String(config.get('SERVER_URL')).replace(/\/+$/, '')
  return appendQuery(
    `${baseUrl}/api/file/download?code=${code}&token=${encodeURIComponent(token)}`,
    'source',
    FILE_SOURCE,
  )
}

/**
 * 下载文件
 * @param url 下载地址
 * @param name 文件url
 */
export function downloadFile(url: string, name: string): void {
  const a = window.document.createElement('a')
  a.href = url
  a.target = '_blank'
  a.style.display = 'none'
  if (name) {
    a.download = name
  }
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

/**
 * 生产唯一值
 */
export function createUUID(): string {
  // 生成RFC4122版本4的UUID，
  // 优先级：crypto.randomUUID → crypto.getRandomValues → Math.random

  //  现代浏览器 或 Node 14.17+
  if (typeof crypto !== 'undefined' && typeof crypto['randomUUID'] === 'function') {
    return crypto['randomUUID']()
  }

  // 支持 crypto.getRandomValues（安全随机）
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    return '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) => {
      const n = Number(c)
      return (n ^ (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (n / 4)))).toString(16)
    })
  }

  // 终极降级（非加密安全）
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * 获取下载文件地址
 * @param code 附件code
 */
export function getDownloadURLx(code: string) {
  const url = String(config.get('SERVER_URL'))
  const startWithHttp: boolean = /(http|https)/.test(code)
  const token = getCachedFileDownloadToken()
  if (startWithHttp) {
    const sep = code.includes('?') ? '&' : '?'
    return appendQuery(`${code}${sep}token=${encodeURIComponent(token)}`, 'source', FILE_SOURCE)
  }
  return appendQuery(
    `${url.replace(/\/+$/, '')}/api/file/download?code=${code}&token=${encodeURIComponent(token)}`,
    'source',
    FILE_SOURCE,
  )
}

export function checkFileExt(filename: string, typeArr: Array<string>) {
  let flag = false // 状态
  const arr = typeArr // 取出上传文件的扩展名
  filename = filename.toLowerCase()
  const index = filename.lastIndexOf('.')
  const ext = filename.substr(index + 1) // 循环比较
  for (let i = 0; i < arr.length; i++) {
    if (ext === arr[i]) {
      flag = true // 一旦找到合适的，立即退出循环
      break
    }
  } // 条件判断
  return flag
}

export function imgTypeList() {
  return [
    'jepg',
    'bmp',
    'jpg',
    'png',
    'tif',
    'gif',
    'pcx',
    'tga',
    'exif',
    'fpx',
    'svg',
    'psd',
    'cdr',
    'pcd',
    'dxf',
    'ufo',
    'eps',
    'ai',
    'raw',
    'webp',
    'jpeg',
  ]
}

/**
 * 下载附件文件数据
 */
export interface FileCodeData {
  code: string
  url?: string
  name?: string
  fileName: string
}

/**
 * 通过code打开附件
 * @param data
 */
export function downloadFileByCode(data: FileCodeData, isDownloadPDF = false) {
  const code = data.code
  const path = data.url
  const name = data.fileName || String(data.name || '')

  let url = ''
  if (!code) {
    if (!path) {
      return Vue.prototype.$message.error('打开附件失败，缺少code')
    } else {
      url = path
    }
  } else {
    url = getDownloadURLx(code)
  }

  const startWithHttp: boolean = /(http|https)/.test(code)
  //code是http或者https链接
  if (startWithHttp) {
    return window.open(code)
  }
  if (checkFileExt(name, imgTypeList())) {
    // 是图片
    Vue.prototype.$privewImg([{ url, fileName: name }])
  } else if (checkFileExt(name, ['mp4'])) {
    Vue.prototype.$privewVideo({ type: 'mp4', url })
  } else if (checkFileExt(name, ['flv'])) {
    Vue.prototype.$privewVideo({ type: 'flv', url })
  } else if (checkFileExt(name, ['pdf']) && !isDownloadPDF) {
    // pdf跳转pdf预览页面
    openPdfToHtmlx(url, name)
  } else {
    window.open(url)
  }
}

/**
 * 通过code打开附件（异步版本，打开前校验文件访问 Token 是否过期/临近过期，必要时自动刷新）
 * 适用于详情页等长停留页面，避免使用本地缓存中已过期的 Token 拼接地址导致无法预览
 * @param data 附件信息；优先使用 code，code 为完整 http(s) 地址时也会追加新鲜 Token
 * @param isDownloadPDF pdf 是否走下载而不是在线预览
 */
export async function downloadFileByCodeWithToken(data: FileCodeData, isDownloadPDF = false) {
  const code = data.code
  const path = data.url
  const name = data.fileName || String(data.name || '')

  if (!code && !path) {
    return Vue.prototype.$message.error('打开附件失败，缺少code')
  }

  // getDownloadURLWithToken 内部会在 Token 不存在/已过期/临近过期时自动刷新
  const url = code ? await getDownloadURLWithToken(code) : String(path || '')
  if (checkFileExt(name, imgTypeList())) {
    // 是图片
    Vue.prototype.$privewImg([{ url, fileName: name }])
  } else if (checkFileExt(name, ['mp4'])) {
    Vue.prototype.$privewVideo({ type: 'mp4', url })
  } else if (checkFileExt(name, ['flv'])) {
    Vue.prototype.$privewVideo({ type: 'flv', url })
  } else if (checkFileExt(name, ['pdf']) && !isDownloadPDF) {
    // pdf跳转pdf预览页面
    openPdfToHtmlx(url, name)
  } else {
    window.open(url)
  }
}

/**
 * 校验是否符合格式要求
 * @param accept 格式
 * @param fileList
 */
export function isAccpetFile(accept: string, fileList: Array<{ name: string; type: string }>) {
  const _accept = accept ? accept.split(',') : []
  if (
    _accept.length &&
    fileList.some((v) => {
      if (v.name || v.type) {
        if (v.type) {
          const type = v.type.split('/')

          const isMatch = _accept.some((match) => {
            const _lower = match.toLowerCase()
            return _lower.includes('*')
              ? _lower.includes(type[0].toLowerCase())
              : _lower.includes(type[1].toLowerCase())
          })

          if (isMatch) {
            return false
          }
        }
        const suffix = v.name.toLocaleLowerCase().split('.').pop()
        return (
          _accept.some((match) => {
            return match.toLowerCase().includes(String(suffix))
          }) === false
        )
      }
    })
  ) {
    return false
  } else return true
}

export function openPdfToHtmlx(url: string, name?: string, returnURL = false) {
  let resultUrl = url
  if (url.includes('?')) {
    resultUrl += `&title=${name || ''}`
  } else {
    resultUrl += `?title=${name || ''}`
  }
  if (!url.startsWith(config.get('SERVER_URL')) && url.startsWith('http:')) {
    // 避免https协议打开第三方http的pdf出现错误，第三方http链接直接打开地址下载
    window.open(url)
  } else {
    const previewUrl = config.get('OFFICE_HALL_URL') + `/pdfjsv2/web/viewer.html?file=${encodeURIComponent(url)}`
    window.open(previewUrl.startsWith('https') ? previewUrl.replace('https', 'http') : previewUrl)
  }
}
